import { Directive, ElementRef, forwardRef, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';
import { HX_FORM_FIELD_CONTROL, HxFormFieldControl } from '../form-group/form-field-control';
import { DateAdapter } from './adapters/abstract-date-adapter';
import { DateFormats, HX_DATE_ADAPTER, HX_DATE_FORMATS } from './date-picker-tokens';

/** @docs-private */
export const HX_DATEPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => HxDatepickerInput),
  multi: true,
};

/** @docs-private */
export const HX_DATEPICKER_VALIDATORS: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => HxDatepickerInput),
  multi: true,
};

/** Event emitted when datepicker input value changes. */
export class HxDatepickerInputEvent<D> {
  /** The new value for the target datepicker input. */
  value: D | null;

  constructor(
    /** Reference to the datepicker input directive. */
    public target: HxDatepickerInput<D>,
    /** Reference to the native input element. */
    public targetElement: HTMLElement
  ) {
    this.value = this.target.value;
  }
}

/**
 * Directive that connects an input element to a datepicker.
 *
 * @example
 *   ```html
 *   <hx-form-group>
 *     <hx-label [description]="'Birth Date'"></hx-label>
 *     <input hxInput [hxDatepicker]="picker" formControlName="birthDate">
 *     <hx-datepicker #picker></hx-datepicker>
 *   </hx-form-group>
 *   ```;
 */
@Directive({
  selector: 'input[hxDatepicker]',
  standalone: true,
  exportAs: 'hxDatepickerInput',
  providers: [
    HX_DATEPICKER_VALUE_ACCESSOR,
    HX_DATEPICKER_VALIDATORS,
    { provide: HX_FORM_FIELD_CONTROL, useExisting: forwardRef(() => HxDatepickerInput) },
  ],
  host: {
    class: 'hx-datepicker-input',
    type: 'text',
    '[attr.aria-haspopup]': '_datepicker ? "dialog" : null',
    '[attr.aria-expanded]': '_datepicker?.isOpen() || null',
    '[attr.aria-owns]': '_ariaOwns()',
    '[attr.min]': 'min ? _formatDateForAttribute(min) : null',
    '[attr.max]': 'max ? _formatDateForAttribute(max) : null',
    '[disabled]': 'disabled',
    '(input)': '_onInput($event)',
    '(change)': '_handleChange()',
    '(blur)': '_onBlur()',
    '(focus)': '_onFocus()',
    '(keydown)': '_onKeydown($event)',
  },
})
// @ts-expect-error - HxFormFieldControl requires id: string, but base class has id: InputSignal<string>. Resolved via Object.defineProperty in constructor.
export class HxDatepickerInput<D>
  extends HxBaseControl
  implements ControlValueAccessor, Validator, HxFormFieldControl<D>, OnInit, OnDestroy
{
  // =========================================================================
  // Dependency Injection
  // =========================================================================

  protected readonly _elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  protected readonly dateAdapter = inject<DateAdapter<D>>(HX_DATE_ADAPTER);
  protected readonly formats = inject<DateFormats>(HX_DATE_FORMATS);

  // =========================================================================
  // Inputs
  // =========================================================================

  /** The datepicker that this input is associated with. */
  @Input('hxDatepicker')
  set datepicker(value: any) {
    if (value) {
      this._datepicker = value;
      this._datepicker.registerInput(this);
      this._ariaOwns.set(this._datepicker.id);
    }
  }
  _datepicker: any;

  /** Minimum selectable date. */
  @Input()
  get min(): D | null {
    return this._min;
  }
  set min(value: D | null) {
    if (!this.dateAdapter.sameDate(value, this._min)) {
      this._min = value;
      this._validatorOnChange();
    }
  }
  private _min: D | null = null;

  /** Maximum selectable date. */
  @Input()
  get max(): D | null {
    return this._max;
  }
  set max(value: D | null) {
    if (!this.dateAdapter.sameDate(value, this._max)) {
      this._max = value;
      this._validatorOnChange();
    }
  }
  private _max: D | null = null;

  /** Function that can be used to filter out dates. */
  @Input('hxDatepickerFilter')
  get dateFilter() {
    return this._dateFilter;
  }
  set dateFilter(value: ((date: D | null) => boolean) | null | undefined) {
    const wasMatchingValue = this._matchesFilter(this.value);
    this._dateFilter = value;
    if (this._matchesFilter(this.value) !== wasMatchingValue) {
      this._validatorOnChange();
    }
  }
  private _dateFilter: ((date: D | null) => boolean) | null | undefined;

  // =========================================================================
  // HxFormFieldControl Implementation - Declared Early
  // =========================================================================

  /** Subject that emits when state changes. */
  readonly stateChanges = new Subject<void>();

  /** Disabled state of the input. */
  @Input()
  // @ts-expect-error - HxFormFieldControl requires readonly disabled: boolean, but base class has readonly disabled: InputSignal
  // This getter/setter satisfies the interface while leveraging the base class disabledState() signal
  get disabled(): boolean {
    // Use base class disabledState() which combines input disabled with CVA disabled
    const baseDisabled = this.disabledState();
    return baseDisabled || this._disabled;
  }
  // @ts-expect-error - HxFormFieldControl requires readonly disabled: boolean, but base class has readonly disabled: InputSignal
  set disabled(value: boolean) {
    const newValue = value != null && `${value}` !== 'false';
    if (newValue !== this._disabled) {
      this._disabled = newValue;
      this.stateChanges?.next();
    }
  }
  private _disabled = false;

  // =========================================================================
  // HxFormFieldControl Implementation
  // =========================================================================

  /** Static counter for generating unique IDs */
  private static _nextId = 0;

  /** Unique ID for the input - satisfies HxFormFieldControl readonly id: string requirement */
  readonly _formFieldId = `hx-datepicker-input-${HxDatepickerInput._nextId++}`;

  /** Control type for form field - different from base class ControlType enum */
  readonly formFieldControlType = 'hx-datepicker-input';

  /** NgControl for form integration (required by HxFormFieldControl) */
  readonly ngControl = inject(NgControl, { optional: true, self: true });

  /** Whether the input is focused. */
  focused = false;

  /** Whether the input is autofilled. */
  autofilled = false;

  /** Current value of the input. */
  get value(): D | null {
    return this._model?.selection || null;
  }
  set value(value: D | null) {
    if (!this.dateAdapter.sameDate(value, this.value)) {
      this._assignValueProgrammatically(value);
    }
  }

  /** Whether input has errors. */
  get errorState(): boolean {
    return !!(this._pendingValue && this._pendingValue !== this.value);
  }

  /** Whether the input is empty. */
  get empty(): boolean {
    return !this._elementRef.nativeElement.value;
  }

  /** Whether the label should float. */
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  /** Implemented as part of HxFormFieldControl. */
  setDescribedByIds(ids: string[]): void {
    this._elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }

  /** Implemented as part of HxFormFieldControl. */
  onContainerClick(): void {
    if (!this.focused) {
      this._elementRef.nativeElement.focus();
    }
  }

  // =========================================================================
  // ControlValueAccessor Implementation
  // =========================================================================

  private _cvaOnChange: (value: any) => void = () => {};
  private _onTouched = () => {};
  private _validatorOnChange = () => {};

  writeValue(value: D): void {
    this._assignValueProgrammatically(value);
  }

  registerOnChange(fn: (value: any) => void): void {
    this._cvaOnChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // =========================================================================
  // Validator Implementation
  // =========================================================================

  validate(control: AbstractControl): ValidationErrors | null {
    return this._validator ? this._validator(control) : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this._validatorOnChange = fn;
  }

  // =========================================================================
  // Internal State
  // =========================================================================

  protected readonly _ariaOwns = signal<string | null>(null);
  protected _model: any;
  private _pendingValue: D | null = null;
  private _localeSubscription = Subscription.EMPTY;
  private _valueChangesSubscription = Subscription.EMPTY;
  private _isInitialized = false;

  /** Min date validator. */
  private readonly _minValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;
    return !this.min || !controlValue || this.dateAdapter.compareDate(this.min, controlValue) <= 0
      ? null
      : { hxDatepickerMin: { min: this.min, actual: controlValue } };
  };

  /** Max date validator. */
  private readonly _maxValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;
    return !this.max || !controlValue || this.dateAdapter.compareDate(this.max, controlValue) >= 0
      ? null
      : { hxDatepickerMax: { max: this.max, actual: controlValue } };
  };

  /** Filter validator. */
  private readonly _filterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;
    return !this._dateFilter || !controlValue || this._dateFilter(controlValue) ? null : { hxDatepickerFilter: true };
  };

  /** Combined validator function. */
  protected _validator: ValidatorFn | null = Validators.compose([
    this._minValidator,
    this._maxValidator,
    this._filterValidator,
  ]);

  // =========================================================================
  // Lifecycle
  // =========================================================================

  constructor() {
    super();
    // Override the 'id' property to satisfy HxFormFieldControl interface (readonly id: string)
    // Base class has 'readonly id: InputSignal<string | undefined>', which is incompatible
    Object.defineProperty(this, 'id', {
      get: () => {
        const baseId = (HxBaseControl.prototype as any).id?.call(this);
        return baseId || this._formFieldId;
      },
      enumerable: true,
      configurable: true,
    });

    // Override the 'required' property to satisfy HxFormFieldControl interface (readonly required: boolean)
    // Base class has 'readonly required: InputSignal<boolean>', which is incompatible
    Object.defineProperty(this, 'required', {
      get: () => {
        const baseRequired = (HxBaseControl.prototype as any).required?.call(this);
        return baseRequired || false;
      },
      enumerable: true,
      configurable: true,
    });
    // Locale changes not supported in current DateAdapter
  }

  ngOnInit(): void {
    this._isInitialized = true;
  }

  ngOnDestroy(): void {
    this._valueChangesSubscription.unsubscribe();
    this._localeSubscription.unsubscribe();
    this.stateChanges.complete();
  }

  // =========================================================================
  // Event Handlers
  // =========================================================================

  _onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const date = value ? this.dateAdapter.parse(value, this.formats.parse) : null;

    this._lastValueValid = !date || this.dateAdapter.isValid(date);
    date ? this._assignValue(date) : this._assignValue(null);
  }

  _handleChange(): void {
    // Emit change event on blur/enter
  }

  _onBlur(): void {
    this._onTouched();
    this.focused = false;
    this.stateChanges.next();
  }

  _onFocus(): void {
    this.focused = true;
    this.stateChanges.next();
  }

  _onKeydown(event: KeyboardEvent): void {
    const isAltDownArrow = event.altKey && event.key === 'ArrowDown';

    if (this._datepicker && isAltDownArrow && !this._elementRef.nativeElement.readOnly) {
      this._datepicker.open();
      event.preventDefault();
    }
  }

  // =========================================================================
  // Helper Methods
  // =========================================================================

  /** Assigns a value to the model and formats it in the input. */
  private _assignValue(value: D | null): void {
    if (!this.dateAdapter.sameDate(value, this._model?.selection)) {
      this._model?.updateSelection(value, this);
      this._cvaOnChange(value);
      this._formatValue(value);
      this.stateChanges.next();
    }
  }

  /** Assigns value programmatically (e.g., from writeValue or locale change). */
  private _assignValueProgrammatically(value: D | null, reformat = false): void {
    const oldValue = this.value;
    this._pendingValue = value;

    if (this._model) {
      this._model.updateSelection(value, this);
    }

    if (reformat || !this.dateAdapter.sameDate(oldValue, value)) {
      this._formatValue(value);
    }

    this.stateChanges.next();
  }

  /** Formats a value and sets it on the input element. */
  private _formatValue(value: D | null): void {
    this._elementRef.nativeElement.value = value ? this.dateAdapter.format(value, this.formats.display) : '';
  }

  /** Formats a date for HTML attribute (ISO 8601). */
  protected _formatDateForAttribute(date: D): string {
    return this.dateAdapter.toIso8601(date);
  }

  /** Whether a value matches the filter function. */
  private _matchesFilter(value: D | null): boolean {
    return !this._dateFilter || !value || this._dateFilter(value);
  }

  /** Whether the last value set on the input was valid. */
  private _lastValueValid = false;
}
